import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { setupApi } from '../../services/setupApi';
import type { CatalogGroup } from '../../services/setupApi';
import { aaiApi } from '../../services/aaiApi';
import { AppMark } from '../../pages/setup/setupKit';
import type { SessionApp } from '../../types/aai';

/*
 * The app logo wall from setup, sized for a session card in the AI
 * Companion: search across every app we support, then the catalog by
 * category. Tapping a logo picks it; the answer is the list of picked apps.
 */

/* The catalog is the same for everyone and never changes while the page is
   open, so every card shares one request. */
let catalogRequest: Promise<CatalogGroup[]> | null = null;
const loadCatalog = (): Promise<CatalogGroup[]> => {
  if (!catalogRequest) {
    catalogRequest = setupApi.catalog().then((r) => r.data.groups).catch(() => {
      catalogRequest = null;
      return [];
    });
  }
  return catalogRequest;
};

const searchCompany = async (query: string): Promise<SessionApp[]> => {
  const { data } = await aaiApi.searchTools(query);
  return data.tools.map((t) => ({ slug: t.slug, label: t.label, logo: t.logo }));
};

export interface AppSource {
  catalog: () => Promise<CatalogGroup[]>;
  search: (query: string) => Promise<SessionApp[]>;
}

export const AppSourceContext = createContext<AppSource>({ catalog: loadCatalog, search: searchCompany });

const Tile: React.FC<{ app: SessionApp; on: boolean; onToggle: (app: SessionApp) => void }> = ({ app, on, onToggle }) => {
  let tone = 'border-line bg-white hover:border-ink-soft';
  if (on) tone = 'border-ink bg-[#eef3ff]';
  return (
    <button
      type="button"
      onClick={() => onToggle(app)}
      aria-pressed={on}
      className={`flex min-w-0 items-center gap-2 rounded-[10px] border px-2.5 py-2 text-left text-[12.5px] font-medium text-ink transition-colors ${tone}`}
    >
      <AppMark app={app} on={on} />
      <span className="truncate">{app.label}</span>
    </button>
  );
};

const Section: React.FC<{ label: string; apps: SessionApp[]; picked: Set<string>; onToggle: (app: SessionApp) => void }> = ({ label, apps, picked, onToggle }) => (
  <section className="mt-3 first:mt-0">
    <b className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</b>
    <div className="grid grid-cols-2 gap-1.5">
      {apps.map((a) => <Tile key={a.slug} app={a} on={picked.has(a.slug)} onToggle={onToggle} />)}
    </div>
  </section>
);

export const AppLogoWall: React.FC<{
  value: SessionApp[];
  onChange: (apps: SessionApp[]) => void;
}> = ({ value, onChange }) => {
  const [groups, setGroups] = useState<CatalogGroup[] | null>(null);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<SessionApp[]>([]);
  const [searching, setSearching] = useState(false);
  const source = useContext(AppSourceContext);

  useEffect(() => {
    let alive = true;
    source.catalog().then((g) => { if (alive) setGroups(g); });
    return () => { alive = false; };
  }, [source]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setFound([]);
      setSearching(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const apps = await source.search(q);
        if (alive) setFound(apps);
      } catch {
        if (alive) setFound([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 300);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      setSearching(false);
    };
  }, [query, source]);

  const picked = useMemo(() => new Set(value.map((a) => a.slug)), [value]);
  const toggle = (app: SessionApp) => {
    if (picked.has(app.slug)) {
      onChange(value.filter((a) => a.slug !== app.slug));
      return;
    }
    onChange([...value, { slug: app.slug, label: app.label, logo: app.logo }]);
  };

  const q = query.trim().toLowerCase();
  const fits = (a: SessionApp) => !q || a.label.toLowerCase().includes(q);
  const catalog = (groups || [])
    .map((g) => ({ ...g, apps: g.apps.filter(fits) }))
    .filter((g) => g.apps.length > 0);
  const listed = new Set((groups || []).flatMap((g) => g.apps.map((a) => a.slug)));
  const more = found.filter((a) => !listed.has(a.slug));
  const pickedElsewhere = value.filter((a) => !listed.has(a.slug) && !more.some((m) => m.slug === a.slug));

  return (
    <div className="mt-3">
      <div className="mb-3 flex items-center gap-2 rounded-[12px] border border-line bg-white px-3 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-ink/10">
        <Search size={14} className="text-ink-soft" aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search every app"
          aria-label="Search apps"
          className="h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ink shadow-none outline-none ring-0 placeholder:text-ink-soft focus:border-0 focus:shadow-none focus:outline-none focus:ring-0"
        />
        {searching && <Loader2 size={13} className="animate-spin text-ink-soft" aria-hidden />}
        <small className="shrink-0 font-mono text-[10.5px] text-ink-muted">{value.length} picked</small>
      </div>
      {pickedElsewhere.length > 0 && <Section label="Your picks" apps={pickedElsewhere} picked={picked} onToggle={toggle} />}
      {more.length > 0 && <Section label="More apps" apps={more} picked={picked} onToggle={toggle} />}
      {catalog.map((g) => <Section key={g.id} label={g.label} apps={g.apps} picked={picked} onToggle={toggle} />)}
      {groups === null && (
        <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-ink-soft" aria-hidden /></div>
      )}
      {groups !== null && catalog.length === 0 && more.length === 0 && !searching && (
        <p className="m-0 py-4 text-center text-[12.5px] text-ink-muted">No app matches "{query}".</p>
      )}
    </div>
  );
};

export default AppLogoWall;
